/**
 * Edit Experience Page
 *
 * Fetches existing experience data in parallel, transforms it to wizard format,
 * and renders ExperienceCreationWizard in edit mode (isNewExperience=false).
 *
 * Story: SCRUM-827 (FIX-F05: Edit-via-wizard route + data transformation)
 * Location: apps/web/src/pages/guide/EditExperiencePage.jsx
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ExperienceCreationWizard from '../../components/experiences/creation/ExperienceCreationWizard';
import apiRequest from '../../services/api';
import { transformToWizardFormat } from '../../utils/experienceTransformations';

export default function EditExperiencePage() {
  const { experienceId } = useParams();
  const navigate = useNavigate();

  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!experienceId) {
      setError('No experience ID provided');
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchData() {
      try {
        // Fetch all data in parallel (SCRUM-832: added routes fetch)
        const [experienceRes, taxonomyRes, pricingRes, sectionsRes, routesRes] = await Promise.allSettled([
          apiRequest(`/api/v1/experiences/${experienceId}`),
          apiRequest(`/api/v1/taxonomy/experiences/${experienceId}`),
          apiRequest(`/api/v1/guides/experiences/${experienceId}/pricing/config`),
          apiRequest(`/api/v1/experiences/${experienceId}/sections`),
          apiRequest(`/api/v1/experiences/${experienceId}/routes`),
        ]);

        if (cancelled) { return; }

        // Experience detail is required — others are optional
        if (experienceRes.status === 'rejected' || !experienceRes.value?.success) {
          throw new Error('Failed to load experience data');
        }

        const experience = experienceRes.value.data;
        const taxonomy = taxonomyRes.status === 'fulfilled' && taxonomyRes.value?.success
          ? taxonomyRes.value.data
          : null;
        const pricingConfig = pricingRes.status === 'fulfilled' && pricingRes.value?.success
          ? pricingRes.value.data
          : null;
        const sections = sectionsRes.status === 'fulfilled' && sectionsRes.value?.success
          ? sectionsRes.value
          : null;
        const routeData = routesRes.status === 'fulfilled' && routesRes.value?.success
          ? routesRes.value.data
          : null;

        const transformed = transformToWizardFormat(experience, taxonomy, pricingConfig, sections, routeData);
        setInitialData(transformed);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load experience');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => { cancelled = true; };
  }, [experienceId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading experience data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <p className="text-red-600 font-semibold mb-4">{error}</p>
          <button
            onClick={() => navigate('/guide/experiences')}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Back to Experiences
          </button>
        </div>
      </div>
    );
  }

  return (
    <ExperienceCreationWizard
      isNewExperience={false}
      initialData={initialData}
    />
  );
}
